import { useEffect, useState } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { useIntl } from 'react-intl-next';

import type { CreatePluginPresetsConfig } from '@atlassian/link-create-presets';
import type { CreatePayload } from '@atlaskit/link-create';

import { createConfluenceDraft } from './utils';
import { useAnalyticsEvents } from '../common/utils/analytics/generated/use-analytics-events';

export const INLINE_CREATE_ONE_STEP_EXPERIMENT = 'confluence_inline_create_one_step_experiment';

export enum INLINE_CREATE_ONE_STEP_COHORTS {
	NOT_ENROLLED = 'not-enrolled',
	CONTROL = 'control',
	VARIANT1 = 'variant1',
	VARIANT2 = 'variant2',
}

export const COHORT = 'cohort';

/*
 * Eligiblity hook for Confluence inline create one step experiment
 */
export const getInlineCreateOneStepExperimentEnabled = ({
	shouldFireExperimentExposure = false,
}: {
	shouldFireExperimentExposure: boolean;
}) => {
	//  Returns the cohort of the tenant: not-enrolled, control, or test
	const cohort = FeatureGates.getExperimentValue<INLINE_CREATE_ONE_STEP_COHORTS>(
		INLINE_CREATE_ONE_STEP_EXPERIMENT,
		COHORT,
		INLINE_CREATE_ONE_STEP_COHORTS.NOT_ENROLLED,
		{ fireExperimentExposure: shouldFireExperimentExposure },
	);

	// Returns if the user should see the inline create one step experiment
	const isInlineCreateOneStepExperimentEnabled =
		cohort === INLINE_CREATE_ONE_STEP_COHORTS.VARIANT1 ||
		cohort === INLINE_CREATE_ONE_STEP_COHORTS.VARIANT2;

	return {
		cohort,
		isInlineCreateOneStepExperimentEnabled,
	};
};

/*
 * Functional component to create a new confluence draft in one step, without any
 * user interaction. Even though we don't render any UI, this has to be a
 * component so that any errors hit the error boundary to show flags and
 * fire the correct analytics.
 */
export const OneStepLinkCreate = ({
	onCreate,
	defaultCloudId,
	gatewayBaseUrl,
	defaultFormValues,
}: {
	onCreate: (payload: CreatePayload) => void;
	defaultCloudId: string;
	gatewayBaseUrl: string | undefined;
	defaultFormValues: CreatePluginPresetsConfig['defaultFormValues'];
}) => {
	// Managing errors via state so that any asynchronous errors (like promise
	// rejections) bubble up to the ErrorBoundary correctly
	const [error, setError] = useState<Error | null>(null);
	if (error) {
		throw error;
	}

	const intl = useIntl();
	const { fireEvent } = useAnalyticsEvents();

	useEffect(() => {
		createConfluenceDraft({
			intl,
			defaultCloudId,
			defaultFormValues,
			gatewayBaseUrl,
			onCreate,
			setError,
			fireAnalyticsEvent: fireEvent,
		});
	}, [intl, defaultCloudId, defaultFormValues, gatewayBaseUrl, onCreate, setError, fireEvent]);

	return null;
};
